import { QuotaTypes } from 'client/Interfaces/Subscription/SubscriptionInterface'
import {
  SubscriptionBillingCycleOptionEnums,
  SubscriptionPlanInterface,
} from 'client/Interfaces/Subscription/SubscriptionPlanInterface'
import { subscriptionAvailablePlansCollectionSelector } from 'client/selectors/subscription'
import { createSelector } from 'reselect'

import { IAvailablePlanNames } from './IPremiumInterface'
import { premiumPlansFeatures } from './PremiumPlanCollection'

interface IOptionsExternal {
  currentSubscription: string | undefined
}

interface IOwnOptions {
  addFreePlan: boolean
  addContactUsPlan: boolean
}

// get quota with name from a plan
export const getPlanQuota = (plan: SubscriptionPlanInterface, quotaName: QuotaTypes) => {
  const quota = plan && plan.quotas.find((i) => i.name === quotaName)
  return quota && quota.quota
}

export const doesPlanHaveExcludedFeatures = (plan: SubscriptionPlanInterface, features: QuotaTypes[]) => {
  const featuresList = premiumPlansFeatures[plan.name].items
  return features.some((feature) => featuresList.some((item) => item.id === feature))
}

const plansFilterSelector = createSelector(
  (plansCollection) => plansCollection,
  (plansCollection, planFilters) => planFilters,
  // todo: any
  (plansCollection: [], planFilters: any) => {
    let filteredPlans = [...plansCollection]

    // filter out plans
    filteredPlans = filteredPlans.filter((plan: any) => {
      if (planFilters.hideFreePlan) {
        // hide free trial plan
        if (plan.name === IAvailablePlanNames['plan.free'] || plan.name === IAvailablePlanNames['plan.ageras_free']) {
          return false
        }
      }

      if (planFilters.billingCycle) {
        if (plan.billingCycle !== planFilters.billingCycle) {
          return false
        }
      }

      if (Array.isArray(planFilters.name)) {
        if (!planFilters.name.some((planName) => planName === plan.name)) {
          return false
        }
      }

      if (Array.isArray(planFilters.excludedFeatures)) {
        if (!doesPlanHaveExcludedFeatures(plan, planFilters.excludedFeatures)) {
          return false
        }
      }

      return true
    })

    return filteredPlans
  },
)

const plansSelector = createSelector(
  (state, options = {}) => {
    const { planFilters = {} } = options

    return plansFilterSelector(subscriptionAvailablePlansCollectionSelector(state), {
      name: planFilters.name || false,
      hideFreePlan: planFilters.hideFreePlan,
      billingCycle: planFilters.billingCycle ? planFilters.billingCycle : SubscriptionBillingCycleOptionEnums.month,
      excludedFeatures: planFilters.excludedFeatures || false,
    })
  },
  (state, options) => options,
  // todo: any
  (plansCollection: SubscriptionPlanInterface[], options: any): any => {
    if (!Array.isArray(plansCollection) || !plansCollection.length) {
      return {
        individualPlans: [],
        groupedPlans: [],
      }
    }

    let transformedPlansCollection: any = [...plansCollection]

    // display plans individually and grouped together (UI reasons), for this, we will create two collections.
    //  one for individual plan, one for grouped plans
    transformedPlansCollection = transformedPlansCollection.reduce(
      (plans, plan) => {
        switch (plan.name) {
          case IAvailablePlanNames['plan.contact_us']:
          case IAvailablePlanNames['plan.merchant']:
          case IAvailablePlanNames['plan.growth']:
          case IAvailablePlanNames['plan.ageras_plus']:
          case IAvailablePlanNames['plan.pro']:
          case IAvailablePlanNames['plan.ageras_basic']: {
            if (
              (plan.name === IAvailablePlanNames['plan.pro'] ||
                plan.name === IAvailablePlanNames['plan.ageras_basic']) &&
              options.planFilters?.cancelation
            ) {
              plans.individualPlans.push(plan)
              break
            }
            plans.groupedPlans.push(plan)
            break
          }
          default:
            plans.individualPlans.push(plan)
        }

        return plans
      },
      {
        individualPlans: [],
        groupedPlans: [],
      },
    )

    return transformedPlansCollection
  },
)

export { plansSelector }
